import { Callout, Heading, Text } from "@imagedesignai/neo";
import { useEffect, useState } from "react";
import type { GenerationSettings } from "../../src/generation-settings.ts";
import type { GifmeApiClient, RunDiagnosticsView } from "./api-client.ts";
import { GenerationSettingsView } from "./generation-settings-view.tsx";
import { formatBytes } from "./library.ts";
import { OwnedVideo, useNearViewport } from "./media-image.tsx";

export interface OutputDiagnosticsProps {
  api: GifmeApiClient;
  runId: string;
  outputMp4: string;
  isRequested: boolean;
  generationSettingsContractVersion: 1;
  generationSettings: GenerationSettings;
}

function DiagnosticVideo({
  api,
  path,
  label,
  description,
  bytes,
}: {
  api: GifmeApiClient;
  path: string;
  label: string;
  description: string;
  bytes?: number;
}) {
  return (
    <article className="diagnostic-card">
      <Text as="span" kind="label" tone="secondary">
        {label}
      </Text>
      <OwnedVideo
        api={api}
        path={path}
        label={label}
        controls
        muted
        loop
        playsInline
        preload="metadata"
      />
      <Text kind="caption" tone="muted">
        {description}
        {bytes === undefined ? "" : ` · ${formatBytes(bytes)}`}
      </Text>
    </article>
  );
}

export function OutputDiagnostics({
  api,
  runId,
  outputMp4,
  isRequested,
  generationSettingsContractVersion,
  generationSettings,
}: OutputDiagnosticsProps) {
  const [diagnostics, setDiagnostics] = useState<RunDiagnosticsView | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const { targetRef, isNearViewport } = useNearViewport<HTMLElement>();
  const isLoadEnabled = isRequested || isNearViewport;

  useEffect(() => {
    if (!isLoadEnabled) return;
    const controller = new AbortController();
    void api
      .getRunDiagnostics(runId, controller.signal)
      .then((value) => {
        if (!controller.signal.aborted) setDiagnostics(value);
      })
      .catch((reason) => {
        if (!(reason instanceof DOMException && reason.name === "AbortError")) {
          setError(reason instanceof Error ? reason.message : String(reason));
        }
      });
    return () => controller.abort();
  }, [api, isLoadEnabled, runId]);

  const submittedLabel =
    diagnostics?.submittedVariant === "previs"
      ? "Submitted input (blur + previs)"
      : diagnostics?.submittedVariant === "blurred"
        ? "Submitted input (blurred)"
        : "Submitted input (original)";

  return (
    <section
      ref={targetRef}
      className="diagnostic-detail"
      aria-label="Output diagnostics"
      aria-busy={isLoadEnabled && diagnostics === undefined && !error}
    >
      <Heading level={2} scale="sm">
        Output diagnostics
      </Heading>
      <Text kind="caption" tone="muted">
        Compare the exact provider input and response with the delivered output.
      </Text>
      <GenerationSettingsView
        contractVersion={generationSettingsContractVersion}
        settings={generationSettings}
      />
      {isLoadEnabled && diagnostics === undefined && !error ? (
        <Text role="status" kind="caption" tone="muted">
          Loading diagnostics…
        </Text>
      ) : null}
      {error ? (
        <Callout intent="danger" label="Could not load diagnostics">
          {error}
        </Callout>
      ) : null}
      {diagnostics === null ? (
        <Callout intent="default" label="Diagnostics unavailable">
          Diagnostic media is unavailable for this run.
        </Callout>
      ) : null}
      {diagnostics ? (
        <>
          <div className="diagnostic-grid">
            <DiagnosticVideo
              api={api}
              path={diagnostics.submittedInput.path}
              label={submittedLabel}
              description="The exact source video sent to the provider"
              bytes={diagnostics.submittedInput.bytes}
            />
            <DiagnosticVideo
              api={api}
              path={diagnostics.providerPreConform.path}
              label="Provider output before conform"
              description="The provider MP4 before source-aspect correction"
              bytes={diagnostics.providerPreConform.bytes}
            />
            <DiagnosticVideo
              api={api}
              path={outputMp4}
              label="Final output"
              description="The delivered MP4 after post-production"
            />
          </div>
          <Callout
            intent={diagnostics.conformApplied ? "warning" : "default"}
            label={
              diagnostics.conformApplied
                ? "Source-aspect conform applied"
                : "Source-aspect conform not needed"
            }
          >
            {diagnostics.conformApplied
              ? "Compare the middle and final videos to see whether conforming introduced the crop."
              : "The provider output already matched the source aspect ratio."}
          </Callout>
        </>
      ) : null}
    </section>
  );
}
